import { useState } from 'react';
import { DateInput, FORM_CONTROL_CLASS, FORM_LABEL_CLASS, FORM_TEXTAREA_CLASS, Modal } from '@safetyhub/ui';
import {
  acceptsWorkerRoster,
  EDUCATION_SPECIAL_OBLIGATION_KEY,
  formatObligationInterval,
  parseLocalToUTC,
  SITE_WORKER_EMPLOYMENT_TYPE_LABELS,
  topicParticleOf,
  workerEducationHours,
  type LegalObligationIntervalVariantView,
  type ObligationInterval,
  type SiteWorkerView,
} from '@safetyhub/shared';
import { EditModalFooter } from '../common/EditModalFooter';
import { EVENT_DEADLINE_REFERENCE } from '../../lib/legalDocumentLabels';
import { fulfilledAtToIso } from './EvidenceModal';
import { todayDateInputInTimezone } from '../../lib/localDate';

/** 서버 DTO의 메모 상한(`LEGAL_OBLIGATION_EVIDENCE_NOTE_MAX`)과 같은 값 */
const DETAIL_MAX_LENGTH = 500;

export interface EventRegisterInput {
  /** UTC ISO — 사건일 현장 시간대 00:00:00 */
  occurredAt: string;
  /** UTC ISO — 기한 현장 시간대 23:59:59 */
  dueAt: string;
  detail: string;
  /** 고른 사건 기한 변형(`EVENT_CHOICE`) — 고르지 않았으면 없다 */
  intervalVariantId?: string;
  /** 특별교육 대상 작업(②-c 2부 E6 — 별표 5 호 번호) — 고르지 않았으면 없다 */
  workTypeCode?: number;
  /** 교육 대상 근로자(M3 ⓐ — 명부) — 고르면 사람마다 항목 · 과정 구분은 보내지 않는다(구분에서 파생) */
  workerIds?: string[];
  /** 단기간·간헐 작업(특별교육 · 근로자를 고른 경우만) */
  shortTermOrIntermittent?: boolean;
}

/** 사건일에 재직한 명부 근로자 — 배치일 ≤ 사건일 ∧ (종료 없음 ∨ 종료일 ≥ 사건일). 서버 `requireActiveSiteWorkers`와 같은 술어 */
export function workersActiveOn(workers: readonly SiteWorkerView[], occurredAtIso: string): SiteWorkerView[] {
  const at = new Date(occurredAtIso).getTime();
  return workers.filter(
    (w) => new Date(w.placedAt).getTime() <= at && (w.endedAt === null || new Date(w.endedAt).getTime() >= at),
  );
}

/** 사건에 고를 수 있는 작업 종류 하나 — 그 현장 작업 명부 중 이 의무에 연결된 분류(`obligationKeys`)만 */
export interface EventWorkTypeOption {
  code: number;
  name: string;
}

export interface EventRegisterModalProps {
  open: boolean;
  obligationKey: string;
  obligationTitle: string;
  /**
   * 카탈로그의 법정 기한 오프셋(②-b D5 — `recurrenceRule.eventDeadline`). 있으면 기한을 `사건일 + 오프셋`으로 **미리 채우고**
   * 사용자가 고칠 수 있다. 없으면(중처법 제4조 · 시정명령) 종전대로 비워 둔다 — 결정 2026-10-29의 근거가 그대로 산다.
   */
  eventDeadline: ObligationInterval | null;
  /** 사건의 속성에 따라 기한이 갈리는 의무의 선택지(`target = EVENT_DEADLINE` · `selector = EVENT_CHOICE`) — 없으면 빈 배열 */
  variants: LegalObligationIntervalVariantView[];
  /**
   * 선택지 중 하나를 **반드시** 골라야 하나(②-g2 G5 — 특별교육 과정 구분 · 서버 `eventChoiceRequired`). 참이면 「해당 없음」을 두지 않고
   * 고를 때까지 등록을 막는다(서버도 400). 생략·false면 종전대로 선택 사항
   */
  choiceRequired?: boolean;
  /**
   * 특별교육 대상 작업 선택지(②-c 2부 E6 · 설계 2차 §5 「현장 명부의 종류만 제시」) — 비거나 생략이면 칸을 그리지 않는다. 고르기는 선택이다
   * (교육 «시간»은 범위 밖 — 기록만 남는다)
   */
  workTypeOptions?: EventWorkTypeOption[];
  /**
   * 근로자 명부(M3 ⓐ) — 명부 대상 의무(shared `acceptsWorkerRoster`)이고 명부에 근로자가 있으면 「교육 대상 근로자」를 고르는 칸을 그린다.
   * 고르면 과정 구분 칸이 사라지고(사람마다 구분에서 파생) 사람마다 교육 시간이 보인다. 고르지 않으면 종전 형태(현장 단위 사건)다
   */
  rosterWorkers?: SiteWorkerView[];
  /** 현장 유효 시간대(서버 `LegalObligationsResponse.timezone`) — 사건일·기한 변환과 「오늘」(2026-11-03 P2) */
  timezone: string;
  submitting: boolean;
  /** 서버 거부 문구 — 모달을 연 채로 보인다 */
  errorMessage: string | null;
  onSubmit: (input: EventRegisterInput) => void;
  onClose: () => void;
}

/**
 * 날짜만 고른 기한을 UTC ISO로 — 시간대 정책(CLAUDE.md): 종료일은 **현장 시간대 23:59:59**를 붙인다
 * (기한 당일은 기한 «안»이다 — 인스턴스 기한 판정의 경계와 같다). 종전 브라우저 로컬(2026-11-03 P2)
 */
export function dueDateToIso(localDate: string, timezone: string): string {
  return parseLocalToUTC(`${localDate}T23:59:59`, timezone).toISOString();
}

/**
 * 로컬 `yyyy-MM-dd` + 간격 → `yyyy-MM-dd`(달 더하기는 말일로 깎는다 — shared `resolveEventLegalDeadline`과 같은 규칙).
 * 미리 채우는 값이지 정본이 아니다(정본은 서버가 현장 시간대로 계산해 응답에 싣는 `legalDueAt`).
 */
export function addIntervalToLocalDate(localDate: string, interval: ObligationInterval): string {
  const [y, m, d] = localDate.split('-').map(Number);
  if (!y || !m || !d) return localDate;
  let date: Date;
  if (interval.unit === 'DAY' || interval.unit === 'WEEK') {
    date = new Date(y, m - 1, d + interval.count * (interval.unit === 'WEEK' ? 7 : 1));
  } else {
    const months = interval.unit === 'YEAR' ? interval.count * 12 : interval.count;
    const total = y * 12 + (m - 1) + months;
    const ny = Math.floor(total / 12);
    const nm = total - ny * 12;
    const lastDay = new Date(ny, nm + 1, 0).getDate();
    date = new Date(ny, nm, Math.min(d, lastDay));
  }
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * 사건 발생 등록(9-i) — 「사건이 생길 때마다 하는」 문서에 사건일·기한·내용을 넣는다. 등록하면 그 사건의 항목이
 * 생기고(기간 = 사건일 ~ 기한) 거기에 문서를 등록해 닫는다.
 *
 * ## 기한 미리 채움(②-b D5 · 사용자 결정 2026-11-02)
 * 카탈로그에 법정 기한 오프셋이 있는 문서(규정 30일 · 선임 보고 14일 · 직무 신규 3개월 …)는 사건일을 고르면 기한이
 * `사건일 + 오프셋`으로 채워지고 **고칠 수 있다**. 사용자가 기한을 손으로 바꾼 뒤에는 사건일을 바꿔도 덮지 않는다.
 * 오프셋이 없는 문서는 종전대로 비워 둔다(결정 2026-10-29 — 중처법 제4조·시정명령은 명령서마다 다르다). 선택지(`variants`)가
 * 있으면(보건관리자가 의사면 1년) 고른 것의 간격으로 채운다 — 서버는 입력이 법정 기한보다 늦어도 거부하지 않고 경고만 돌려준다.
 */
export default function EventRegisterModal({
  open,
  obligationKey,
  obligationTitle,
  eventDeadline,
  variants,
  choiceRequired = false,
  workTypeOptions = [],
  rosterWorkers = [],
  timezone,
  submitting,
  errorMessage,
  onSubmit,
  onClose,
}: EventRegisterModalProps) {
  const today = todayDateInputInTimezone(timezone);
  const [occurredOn, setOccurredOn] = useState(today);
  const [variantId, setVariantId] = useState('');
  const [workTypeCode, setWorkTypeCode] = useState('');
  // M3 ⓐ — 근로자 갈래. 고른 근로자 중 사건일에 재직하지 않는 사람은 보내지 않는다(사건일을 바꾸면 선택지가 바뀐다)
  const rosterEnabled = acceptsWorkerRoster(obligationKey) && rosterWorkers.length > 0;
  const activeWorkers = rosterEnabled && occurredOn ? workersActiveOn(rosterWorkers, fulfilledAtToIso(occurredOn, timezone)) : [];
  const [pickedWorkerIds, setPickedWorkerIds] = useState<string[]>([]);
  const workerIds = pickedWorkerIds.filter((id) => activeWorkers.some((w) => w.id === id));
  const byWorkers = workerIds.length > 0;
  const isSpecial = obligationKey === EDUCATION_SPECIAL_OBLIGATION_KEY;
  const [shortTerm, setShortTerm] = useState(false);
  const toggleWorker = (id: string) =>
    setPickedWorkerIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  // 근로자를 고르면 사건 단위 과정 구분은 쓰지 않는다(서버도 둘을 함께 받지 않는다) — 기한 미리 채움은 기본 오프셋으로
  const chosen = byWorkers ? null : (variants.find((v) => v.id === variantId) ?? null);
  const effectiveDeadline = chosen?.interval ?? eventDeadline;
  const suggested = effectiveDeadline ? addIntervalToLocalDate(occurredOn, effectiveDeadline) : '';
  // 기한을 손으로 고친 뒤에는 미리 채움이 덮지 않는다 — `null`이면 「아직 안 고쳤다」
  const [dueOverride, setDueOverride] = useState<string | null>(null);
  const dueOn = dueOverride ?? suggested;
  const [detail, setDetail] = useState('');
  const trimmed = detail.trim();
  const reference = EVENT_DEADLINE_REFERENCE[obligationKey];

  const isDirty =
    dueOverride !== null ||
    trimmed !== '' ||
    occurredOn !== today ||
    variantId !== '' ||
    workTypeCode !== '' ||
    pickedWorkerIds.length > 0 ||
    shortTerm;
  const mustChoose = choiceRequired && variants.length > 0 && !byWorkers;
  const blockedReason = !occurredOn
    ? '사건일을 선택하세요.'
    : mustChoose && !variantId
      ? '해당 사항을 선택하세요.'
      : !dueOn
      ? '기한을 선택하세요.'
      : dueOn < occurredOn
        ? '기한이 사건일보다 앞설 수 없습니다.'
        : !trimmed
          ? '어떤 사건인지 내용을 적으세요.'
          : '';

  const handleSubmit = () => {
    if (blockedReason || submitting) return;
    onSubmit({
      occurredAt: fulfilledAtToIso(occurredOn, timezone),
      dueAt: dueDateToIso(dueOn, timezone),
      detail: trimmed,
      ...(variantId && !byWorkers ? { intervalVariantId: variantId } : {}),
      ...(workTypeCode ? { workTypeCode: Number(workTypeCode) } : {}),
      ...(byWorkers ? { workerIds } : {}),
      ...(byWorkers && isSpecial && shortTerm ? { shortTermOrIntermittent: true } : {}),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="사건 등록"
      size="md"
      closeLocked={submitting}
      isDirty={isDirty}
      footer={
        <EditModalFooter
          isDirty={isDirty}
          blockedReason={blockedReason}
          submitLabel="사건 등록"
          submitDisabled={!!blockedReason || submitting}
          busy={submitting}
          busyLabel="등록 중..."
          onSubmit={handleSubmit}
          onFallbackClose={onClose}
        />
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-700">
          <span className="font-bold text-ink-900">{obligationTitle}</span>
          {topicParticleOf(obligationTitle)} 사건이 생길 때마다 하는
          문서입니다. 사건을 등록하면 기한까지 문서를 등록할 항목이 생깁니다.
        </p>

        <div>
          <label htmlFor="legal-event-occurred-on" className={FORM_LABEL_CLASS}>
            사건일
          </label>
          <DateInput
            id="legal-event-occurred-on"
            label="사건일"
            value={occurredOn}
            onChange={setOccurredOn}
            max={today}
            disabled={submitting}
            className="w-full"
          />
        </div>

        {rosterEnabled && (
          <fieldset data-testid="legal-event-workers">
            <legend className={FORM_LABEL_CLASS}>교육 대상 근로자 (선택)</legend>
            {activeWorkers.length === 0 ? (
              <p className="text-xs text-ink-500">사건일에 재직한 명부 근로자가 없습니다.</p>
            ) : (
              <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto rounded-[10px] border border-ink-100 p-2">
                {activeWorkers.map((w) => {
                  const hours = workerEducationHours(obligationKey, w.employmentType, {
                    shortTermOrIntermittent: shortTerm,
                    workTypeCode: workTypeCode ? Number(workTypeCode) : null,
                  });
                  return (
                    <li key={w.id}>
                      <label className="flex items-center gap-2 text-sm text-ink-800">
                        <input
                          type="checkbox"
                          checked={pickedWorkerIds.includes(w.id)}
                          onChange={() => toggleWorker(w.id)}
                          disabled={submitting}
                        />
                        <span className="font-semibold">{w.name}</span>
                        <span className="text-xs text-ink-500">
                          {SITE_WORKER_EMPLOYMENT_TYPE_LABELS[w.employmentType]}
                          {hours !== null ? ` · ${hours}시간 이상` : ''}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
            <p className="mt-1 text-xs text-ink-500">
              고르면 사람마다 항목이 생기고 교육 시간·둘째 기한이 고용 구분([별표 4] 제1호)에서 정해집니다. 고르지 않으면 현장 단위 사건 하나로
              등록됩니다. 상시근로자 50명 미만 도매·숙박·음식점업 등 [별표 4] 비고 제3호 사업은 시간이 ½입니다.
            </p>
            {isSpecial && byWorkers && (
              <label className="mt-2 flex items-center gap-2 text-sm text-ink-800">
                <input type="checkbox" checked={shortTerm} onChange={(e) => setShortTerm(e.target.checked)} disabled={submitting} />
                단기간 작업 또는 간헐적 작업(일용·1주 이하 기간제가 아닌 근로자도 2시간 · 둘째 기한 없음)
              </label>
            )}
          </fieldset>
        )}

        {variants.length > 0 && !byWorkers && (
          <div>
            <label htmlFor="legal-event-variant" className={FORM_LABEL_CLASS}>
              {mustChoose ? '해당 사항 (필수)' : '해당 사항'}
            </label>
            <select
              id="legal-event-variant"
              className={FORM_CONTROL_CLASS}
              value={variantId}
              onChange={(e) => setVariantId(e.target.value)}
              disabled={submitting}
            >
              {mustChoose ? (
                <option value="" disabled>
                  선택하세요
                </option>
              ) : (
                <option value="">해당 없음{eventDeadline ? ` (기본 — ${formatObligationInterval(eventDeadline)} 이내)` : ''}</option>
              )}
              {variants.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label ?? formatObligationInterval(v.interval)} ({formatObligationInterval(v.interval)} 이내)
                </option>
              ))}
            </select>
          </div>
        )}

        {workTypeOptions.length > 0 && (
          <div>
            <label htmlFor="legal-event-work-type" className={FORM_LABEL_CLASS}>
              대상 작업 (선택)
            </label>
            <select
              id="legal-event-work-type"
              className={FORM_CONTROL_CLASS}
              value={workTypeCode}
              onChange={(e) => setWorkTypeCode(e.target.value)}
              disabled={submitting}
            >
              <option value="">고르지 않음</option>
              {workTypeOptions.map((w) => (
                <option key={w.code} value={String(w.code)}>
                  {w.code}. {w.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-ink-500">이 현장 작업 명부에 등록한 특별교육 대상 작업만 보입니다.</p>
          </div>
        )}

        <div>
          <label htmlFor="legal-event-due-on" className={FORM_LABEL_CLASS}>
            기한
          </label>
          <DateInput
            id="legal-event-due-on"
            label="기한"
            value={dueOn}
            onChange={setDueOverride}
            min={occurredOn || undefined}
            disabled={submitting}
            className="w-full"
          />
          <p className="mt-1 text-xs text-ink-500">
            {effectiveDeadline
              ? `법정 기한: 사건일부터 ${effectiveDeadline.count === 0 ? '당일' : `${formatObligationInterval(effectiveDeadline)} 이내`}(${suggested}) — 미리 채웠고 고칠 수 있습니다.`
              : (reference ?? '법령·명령서에 정해진 기한을 직접 입력하세요.')}
          </p>
        </div>

        <div>
          <label htmlFor="legal-event-detail" className={FORM_LABEL_CLASS}>
            내용 (필수)
          </label>
          <textarea
            id="legal-event-detail"
            className={FORM_TEXTAREA_CLASS}
            value={detail}
            maxLength={DETAIL_MAX_LENGTH}
            onChange={(e) => setDetail(e.target.value)}
            disabled={submitting}
            placeholder="예: 3공장 프레스 협착 사고(작업자 1명 경상)"
          />
        </div>

        {errorMessage && (
          <p
            role="alert"
            className="rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
          >
            {errorMessage}
          </p>
        )}
      </div>
    </Modal>
  );
}
